import React from 'react';
import { Separator } from '@/components/ui/separator';
import { formatDateTime12, formatDateTime24 } from '@/utils/helpers';
import type { AlertTriageListRow } from './PulseOximeterAlertView';

const nonEmpty = (v: unknown) =>
  v != null && String(v).trim() !== "" ? String(v).trim() : "";

interface BloodPressureAlertViewProps {
  data: any;
  listRow?: AlertTriageListRow | null;
}

/**
 * Formats a camelCase or PascalCase key into a readable question format
 * Example: "heartRate" -> "Heart Rate"
 */
const formatQuestionKey = (key: string): string => {
  return key
    .replace(/([A-Z])/g, ' $1')
    .replace(/^./, (str) => str.toUpperCase())
    .trim();
};

const BloodPressureAlertView: React.FC<BloodPressureAlertViewProps> = ({ data, listRow }) => {
  const bloodPressureData = data?.data?.forms?.[0]?.bloodPressureData;
  if (!bloodPressureData) return null;

  const questions: Array<{ question: string; answer: string | number | null | undefined }> = [];
  const showText = (v: unknown) => (nonEmpty(v) ? String(v) : "—");
  const showYesNo = (v: unknown) =>
    v === "yes" || v === true ? "Yes" : v === "no" || v === false ? "No" : "—";

  // Add Blood Pressure Type
  if (bloodPressureData.bpType) {
    questions.push({ 
      question: 'Blood Pressure Type', 
      answer: bloodPressureData.bpType === 'high' ? 'High' : 'Low' 
    });
  }

  // Process high or low blood pressure data
  const highBpData = bloodPressureData.highBloodPressure;
  const lowBpData = bloodPressureData.lowBloodPressure;

  if (highBpData) {
    // Is blood pressure >180 systolically and/or >110 diastolically?
    if (highBpData.isBloodPressureOver180) {
      questions.push({
        question: 'Is blood pressure >180 systolically and/or >110 diastolically?',
        answer: highBpData.isBloodPressureOver180 === 'yes' ? 'Yes' : 'No'
      });
    }

    // Medication Taken
    if (highBpData.medicationTaken) {
      questions.push({
        question: 'Have you taken your blood pressure medication?',
        answer: highBpData.medicationTaken === 'yes' ? 'Yes' : 'No'
      });
    }

    // Medication Details (if medication taken)
    if (highBpData.medicationTaken === 'yes' && highBpData.medicationDetails) {
      questions.push({
        question: 'Medication Details',
        answer: highBpData.medicationDetails
      });
    }

    // Crisis Path (when BP >180)
    if (highBpData.crisisPath) {
      const crisisPath = highBpData.crisisPath;

      // Have you ever experienced a hypertensive crisis?
      if (crisisPath.hasExperiencedHypertensiveCrisis) {
        questions.push({
          question: 'Have you ever experienced a hypertensive crisis in the past?',
          answer: crisisPath.hasExperiencedHypertensiveCrisis === 'yes' ? 'Yes' : 'No'
        });
      }

      // Hypertensive Crisis Explanation
      if (crisisPath.hasExperiencedHypertensiveCrisis === 'yes' && crisisPath.hypertensiveCrisisExplanation) {
        questions.push({
          question: 'Please explain your hypertensive crisis experience:',
          answer: crisisPath.hypertensiveCrisisExplanation
        });
      }

      // How do you feel?
      if (crisisPath.howDoYouFeel) {
        questions.push({
          question: 'How do you feel?',
          answer: crisisPath.howDoYouFeel
        });
      }

      // Symptoms
      if (crisisPath.symptoms) {
        const symptoms = crisisPath.symptoms;
        const symptomLabels: Record<string, string> = {
          severeChestPain: 'Severe Chest Pain',
          nausea: 'Nausea',
          vomiting: 'Vomiting',
          fainting: 'Fainting',
          waterRetention: 'Water Retention',
          dizziness: 'Dizziness',
          fatigue: 'Fatigue',
          severeHeadache: 'Severe Headache',
          blurredVision: 'Blurred Vision',
          confusion: 'Confusion',
          severeAnxiety: 'Severe Anxiety',
          seizure: 'Seizure',
          unresponsiveness: 'Unresponsiveness',
          noSymptoms: 'No Symptoms'
        };

        // Add individual symptoms
        let ansArr = [];

        for (const [key, value] of Object.entries(symptoms)) {
          if (key !== 'noSymptoms' && (value === 'yes' || value === 'no')) {
            if(value === 'yes') {
              ansArr.push(symptomLabels[key] || formatQuestionKey(key));
            }
            // questions.push({
            //   question: symptomLabels[key] || formatQuestionKey(key),
            //   answer: value === 'yes' ? 'Yes' : 'No'
            // });
          }
        }
        if(ansArr.length > 0) {
        questions.push({
          question: 'Symptoms',
          answer: ansArr.join(', ')
        });
        }

        // No Symptoms checkbox
        if (symptoms.noSymptoms === true) {
          questions.push({
            question: 'The patient is not experiencing any signs/symptoms of a hypertensive crisis',
            answer: 'Yes'
          });
        }
      }

      // Recommendations
      if (crisisPath.recommendations) {
        const recommendations = crisisPath.recommendations;
        const recommendationLabels: Record<string, string> = {
          improveDiet: 'Improve Diet',
          decreaseSalt: 'Decrease Salt',
          dailyExercise: 'Daily Exercise',
          weightLoss: 'Weight Loss',
          reduceCaffeine: 'Reduce Caffeine',
          reduceAlcohol: 'Reduce Alcohol',
          reduceSmoking: 'Reduce Smoking',
          reduceStress: 'Reduce Stress',
          takeMedication: 'Take Medication',
          monitorBpDaily: 'Monitor BP Daily',
          escalateToProvider: 'Escalate to Provider',
          takeBpMorning: 'Take BP in the Morning'
        };

        const activeRecommendations = Object.entries(recommendations)
          .filter(([, value]) => value === true)
          .map(([key]) => recommendationLabels[key] || formatQuestionKey(key));

        if (activeRecommendations.length > 0) {
          questions.push({
            question: 'Recommendations',
            answer: activeRecommendations.join(', ')
          });
        }
      }
    }

    // Follow-up Path (when BP <=180)
    if (highBpData.followUpPath) {
      const followUpPath = highBpData.followUpPath;

      // Process follow-up questions
      for (const [key, value] of Object.entries(followUpPath)) {
        if (key === 'recommendations' || value === null) continue;

        if (typeof value === 'string' || typeof value === 'number') {
          questions.push({
            question: formatQuestionKey(key),
            answer: String(value)
          });
        }
      }

      // Follow-up Recommendations
      if (followUpPath.recommendations) {
        const recommendations = followUpPath.recommendations;
        const recommendationLabels: Record<string, string> = {
          improveDiet: 'Improve Diet',
          decreaseSalt: 'Decrease Salt',
          dailyExercise: 'Daily Exercise',
          weightLoss: 'Weight Loss',
          reduceCaffeine: 'Reduce Caffeine',
          reduceAlcohol: 'Reduce Alcohol',
          reduceSmoking: 'Reduce Smoking',
          reduceStress: 'Reduce Stress',
          takeMedication: 'Take Medication',
          monitorBpDaily: 'Monitor BP Daily',
          escalateToProvider: 'Escalate to Provider',
          takeBpMorning: 'Take BP in the Morning'
        };

        if (typeof recommendations === 'object') {
          const activeRecommendations = Object.entries(recommendations)
            .filter(([, value]) => value === true)
            .map(([key]) => recommendationLabels[key] || formatQuestionKey(key));

          if (activeRecommendations.length > 0) {
            questions.push({
              question: 'Recommendations',
              answer: activeRecommendations.join(', ')
            });
          }
        }
      }
    }
  }

  // Handle Low Blood Pressure
  if (lowBpData) {
    // Low BP: show key fields with correct question text (match save flow wording)
    if (lowBpData.isBloodPressureUnder90) {
      questions.push({
        question: "Is blood pressure <90 systolically and/or <60 diastolically ?",
        answer: lowBpData.isBloodPressureUnder90 === "yes" ? "Yes" : "No",
      });
    }

    if (lowBpData.medicationTaken) {
      questions.push({
        question: "Have you taken your blood pressure medication?",
        answer: lowBpData.medicationTaken === "yes" ? "Yes" : "No",
      });
    }

    if (lowBpData.medicationTaken === "yes" && nonEmpty(lowBpData.medicationDetails)) {
      questions.push({
        question: "Medication Details",
        answer: String(lowBpData.medicationDetails),
      });
    }

    // Low BP Crisis Path (this is the data you shared but was not displayed)
    if (lowBpData.crisisPath) {
      const crisisPath = lowBpData.crisisPath;

      if (crisisPath.hasExperiencedHypotensiveCrisis) {
        questions.push({
          question: "Have you ever experienced a hypotensive crisis in the past?",
          answer: showYesNo(crisisPath.hasExperiencedHypotensiveCrisis),
        });
      } else {
        questions.push({
          question: "Have you ever experienced a hypotensive crisis in the past?",
          answer: "—",
        });
      }

      // Always show explanation field (even if empty) so saved structure is visible
      questions.push({
        question: "Please explain your hypotensive crisis experience:",
        answer: showText(crisisPath.hypotensiveCrisisExplanation),
      });

      // Always show "How do you feel?" field
      questions.push({
        question: "How do you feel?",
        answer: showText(crisisPath.howDoYouFeel),
      });

      let ansArr2: string[] = [];

      if (crisisPath.symptoms) {
        const symptoms = crisisPath.symptoms;
        const symptomLabels: Record<string, string> = {
          rapidPulse: "Rapid Pulse",
          shallowBreathing: "Shallow Breathing",
          coldSkin: "Cold Skin",
          fainting: "Fainting",
          confusion: "Confusion",
          bleeding: "Bleeding",
          noSymptoms: "No Symptoms",
        };

        // Always render each symptom key (even if empty), showing — when unanswered.
        (["rapidPulse", "shallowBreathing", "coldSkin", "fainting", "confusion", "bleeding"] as const).forEach(
          (key) => {
            const value = (symptoms as any)?.[key];
            if(value === 'yes') {
              ansArr2.push(symptomLabels[key] || formatQuestionKey(key));
            }
            // questions.push({
            //   question: symptomLabels[key] || formatQuestionKey(key),
            //   answer: showYesNo(value),
            // });
          },
        );

        if(ansArr2.length > 0) {
        questions.push({
          question: 'Symptoms',
          answer: ansArr2.join(', ')
        });
        }

        // Always show the "noSymptoms" checkbox state
        questions.push({
          question:
            "The patient is not experiencing any signs/symptoms of a hypotensive crisis",
          answer: showYesNo((symptoms as any)?.noSymptoms),
        });
      }
    }

    // Handle low BP follow-up path
    if (lowBpData.followUpPath) {
      for (const [key, value] of Object.entries(lowBpData.followUpPath)) {
        if (key === 'recommendations' || value === null) continue;

        if (typeof value === 'string' || typeof value === 'number') {
          questions.push({
            question: formatQuestionKey(key),
            answer: String(value)
          });
        }
      }

      // Low BP Recommendations
      if (lowBpData.followUpPath.recommendations) {
        const recommendations = lowBpData.followUpPath.recommendations;
        if (typeof recommendations === 'object') {
          const activeRecommendations = Object.entries(recommendations)
            .filter(([, value]) => value === true)
            .map(([key]) => formatQuestionKey(key))
            .join(', ');

          if (activeRecommendations) {
            questions.push({
              question: 'Recommendations',
              answer: activeRecommendations
            });
          }
        }
      }
    }
  }

  if (questions.length === 0) return null;

  const formData = data?.data?.forms?.[0];
  const patient = formData?.patientId;
  const patientName = patient ? [patient.firstName, patient.middleName, patient.lastName].filter(Boolean).join(' ') : '';
  const timestampRaw =
    formData?.createdAt ?? data?.data?.createdAt ?? data?.data?.triageTime;
  const timestamp = timestampRaw ? formatDateTime12(timestampRaw) : '';
  const timestampSafe = nonEmpty(listRow?.timestamp) || timestamp || "—";
  const deviceTypeRaw = formData?.deviceType ?? data?.data?.deviceType ?? '';
  const deviceDisplay = deviceTypeRaw
    ? deviceTypeRaw
        .replace(/_/g, ' ')
        .split(' ')
        .filter(Boolean)
        .map((word: string) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
        .join(' ')
    : '';
  const deviceDisplaySafe = nonEmpty(listRow?.device) || deviceDisplay || "—";

  const readingValues = formData?.readingValues ?? data?.data?.readingValues ?? {};
  const readingValuesFormatted =
    readingValues && typeof readingValues === 'object' && !Array.isArray(readingValues)
      ? Object.entries(readingValues)
          .map(([key, value]) => {
            const formattedKey =
              key && typeof key === 'string'
                ? key.charAt(0).toUpperCase() + key.slice(1).replace(/_/g, ' ')
                : String(key);
            return `${formattedKey}: ${value}`;
          })
          .join(', ')
      : '';
  const readingId = formData?.readingId ?? data?.data?.readingId;
  const readingFromId =
    typeof readingId === 'object' && readingId !== null
      ? readingId?.readingId ?? readingId?._id ?? ''
      : readingId ?? '';
  const readingDisplay = readingValuesFormatted || (readingFromId ? String(readingFromId) : '');
  const readingDisplaySafe =
    nonEmpty(listRow?.reading) || readingDisplay || "—";
  const submittedBy = formData?.submittedBy;
  const submittedByName = submittedBy?.name || '';

  return (
    <div className="bg-gray-100 min-h-screen py-8 px-4">
      <div className="bg-white shadow-lg" style={{ 
        width: '595px', 
        maxWidth: '100%', 
        margin: '0 auto', 
        boxSizing: 'border-box',
        padding: '40px',
        minHeight: '842px',
        boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)'
      }}>
        <div className="header mb-8 bg-[#eeeeee] p-5 rounded-md">
          <h2 className="font-medium teachers mb-4 text-lg">
            Triage View
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {patientName && (
              <div>
                <span className="font-semibold text-[#6E6E6E] teachers text-sm">PATIENT NAME:</span>
                <span className="ml-2 text-black text-sm teachers">{patientName}</span>
              </div>
            )}
            {submittedByName && (
              <div>
                <span className="font-semibold text-[#6E6E6E] teachers text-sm">SUBMITTED BY:</span>
                <span className="ml-2 text-black text-sm teachers">{submittedByName}</span>
              </div>
            )}
          </div>
          {submittedByName && (
            <div className="mt-4 flex flex-col gap-2">
              <div>
                <span className="font-semibold text-[#6E6E6E] teachers text-sm">READING:</span>
                <span className="ml-2 text-black text-sm teachers">{readingDisplaySafe}</span>
              </div>
              <div>
                <span className="font-semibold text-[#6E6E6E] teachers text-sm">DEVICE:</span>
                <span className="ml-2 text-black text-sm teachers">{deviceDisplaySafe}</span>
              </div>
              <div>
                <span className="font-semibold text-[#6E6E6E] teachers text-sm">TIMESTAMP:</span>
                <span className="ml-2 text-black text-sm teachers">{timestampSafe}</span>
              </div>
            </div>
          )}
        </div>
        <h3 className="font-medium teachers mb-6 text-base">
          Blood Pressure Triage Form
        </h3>
        <div className="space-y-0">
          {questions.map((item, index) => (
            <div key={index} className="mb-4">
              <div className="teachers text-xs mb-2.5">{item.question}</div>
              {item.answer && (
                <div className="font-medium teachers text-xs">
                  {item.answer}
                </div>
              )}
              <Separator className='bg-[#dddddd] my-2'/>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

export default BloodPressureAlertView;
